import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import StatusTracker from './StatusTracker';
import './howItWorks.css';

// In-app "How it works" tutorial for Build-your-app. Hand-built (no markdown
// runtime) so it stays on the design system and light on the bundle. The one
// lifecycle flow diagram is rendered with Mermaid, lazy-loaded only when the
// panel is open. Mirrors docs/BUILD_YOUR_APP_GUIDE.md in a scannable form.

const FLOW_DIAGRAM = `flowchart LR
    A([You submit]) --> B{Admin reviews}
    B -- Approved --> C[Admin sets up
    secure access]
    C --> D([In progress — you build])
    D --> E([Request publish])
    E --> F{Admin reviews}
    F -- Needs changes --> D
    F -- Approved --> G([Published])
    B -- Rejected --> X([Rejected])
    style A fill:#FFF1F0,stroke:#E9453E
    style D fill:#F0EAD6,stroke:#8A7322
    style G fill:#E7ECEB,stroke:#4F6360`;

const STEPS = [
  {
    title: 'Submit a request',
    body: 'Name the app, describe it, pick the tools it needs, and say how it’ll use each one. Add teammates as collaborators if you want help. Google SSO is always included automatically.',
  },
  {
    title: 'Wait for review',
    body: 'An admin approves (and sets up secure access) or rejects with a note. While it’s pending you can still edit or cancel it.',
  },
  {
    title: 'Build it (In progress)',
    body: 'Once access is ready your app is In progress. You and your collaborators can reveal each reference’s value (fetched live, shown briefly, logged) and wire the app up.',
  },
  {
    title: 'Request publishing',
    body: 'When it’s ready, supply the production URL + description. An admin reviews and either gives feedback (back to In progress) or publishes it to the company catalog. 🎉',
  },
];

const ROLES = [
  { label: 'See the app & references', creator: true, collaborator: true },
  { label: 'Reveal a reference value', creator: true, collaborator: true },
  { label: 'Request publishing', creator: true, collaborator: true },
  { label: 'Comment in the thread', creator: true, collaborator: true },
  { label: 'Edit / cancel while pending', creator: true, collaborator: false },
];

// Renders one Mermaid diagram. Lazy-imports mermaid + dompurify on mount,
// renders to SVG, sanitizes, injects. Falls back to a plain code block.
function MermaidDiagram({ code }) {
  const ref = useRef(null);
  const rawId = useId();
  const domId = `mmd-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          // Render labels as real SVG <text>, NOT HTML in <foreignObject>.
          // DOMPurify's SVG profile strips foreignObject + its HTML spans,
          // which left every node/edge label blank. SVG <text> survives
          // sanitization and obeys the `fill` override in howItWorks.css.
          flowchart: { htmlLabels: false },
          themeVariables: {
            // Node fills + borders
            primaryColor: '#FFF1F0',
            primaryBorderColor: '#E9453E',
            secondaryColor: '#F4F2E3',
            secondaryBorderColor: '#A8A8A0',
            tertiaryColor: '#FFFFFF',
            tertiaryBorderColor: '#A8A8A0',
            // Text — make every label dark ink so it's legible on the light fills
            primaryTextColor: '#151514',
            secondaryTextColor: '#151514',
            tertiaryTextColor: '#151514',
            textColor: '#151514',
            nodeTextColor: '#151514',
            // Edges + their labels (the "Approved"/"Rejected" words)
            lineColor: '#A8A8A0',
            edgeLabelBackground: '#FFFDEC',
            labelBackground: '#FFFDEC',
            labelTextColor: '#151514',
            fontFamily: 'Raleway, system-ui, sans-serif',
            fontSize: '14px',
          },
        });
        const { svg } = await mermaid.render(domId, code);
        if (cancelled || !ref.current) return;
        // The diagram source is a hardcoded developer constant (FLOW_DIAGRAM),
        // not user input, and mermaid runs with securityLevel:'strict' which
        // already sanitizes its own output. Inject the SVG directly — a second
        // DOMPurify pass (SVG profile) mangled mermaid v11's node-label markup
        // (<text>/<tspan> + scoped <style>), leaving the in-node labels blank.
        // The .hiw-diagram CSS fill overrides keep text legible regardless.
        ref.current.innerHTML = svg;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [code, domId]);

  if (failed) return <pre className="hiw-code">{code}</pre>;
  return <div className="hiw-diagram" ref={ref} aria-label="App lifecycle diagram" role="img" />;
}

function Check({ on }) {
  return <span className={`hiw-check${on ? ' is-on' : ''}`} aria-label={on ? 'yes' : 'no'}>{on ? '✓' : '—'}</span>;
}

export default function HowItWorks({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const closeBtnRef = useRef(null);
  const lastFocusRef = useRef(null);
  const openDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);

  // Esc closes the drawer; focus moves to the close button on open and is
  // restored to the trigger on close.
  useEffect(() => {
    if (!open) return undefined;
    lastFocusRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); } };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const el = lastFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open, closeDrawer]);

  return (
    <section className="hiw">
      <button
        type="button"
        className="hiw-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={openDrawer}
      >
        <span className="hiw-toggle-icon" aria-hidden="true">?</span>
        <span className="hiw-toggle-text">
          <span className="hiw-toggle-title">How it works</span>
          <span className="hiw-toggle-sub">A quick tour of building &amp; publishing an app</span>
        </span>
        <span className="hiw-chevron" aria-hidden="true">›</span>
      </button>

      <div
        className={`hiw-backdrop${open ? ' is-open' : ''}`}
        aria-hidden="true"
        onClick={closeDrawer}
      />
      <aside
        className={`hiw-drawer${open ? ' is-open' : ''}`}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-label="How Build your app works"
        aria-hidden={!open}
        // Closed, the drawer is only slid off-screen (it stays mounted so it can
        // animate); inert takes its close button and links out of the tab order.
        {...(open ? {} : { inert: '' })}
      >
        <header className="hiw-drawer-head">
          <span className="hiw-toggle-icon" aria-hidden="true">?</span>
          <span className="hiw-drawer-head-text">
            <span className="hiw-toggle-title">How it works</span>
            <span className="hiw-toggle-sub">Building &amp; publishing an app</span>
          </span>
          <button
            ref={closeBtnRef}
            type="button"
            className="hiw-drawer-close"
            aria-label="Close"
            onClick={closeDrawer}
          >
            ×
          </button>
        </header>

        <div className="hiw-body">
          <p className="hiw-lead">
            <strong>Build your app</strong> turns the tools and data you already use into an internal
            app — no waiting on a backlog. You describe <em>what</em> you want; an admin wires up the
            secure <em>how</em>.
          </p>

          <h3 className="hiw-h">The lifecycle</h3>
          <StatusTracker status="active" />
          <MermaidDiagram code={FLOW_DIAGRAM} />

          <h3 className="hiw-h">What you do</h3>
          <ol className="hiw-steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="hiw-step">
                <span className="hiw-step-num" aria-hidden="true">{i + 1}</span>
                <span className="hiw-step-text">
                  <strong className="hiw-step-title">{s.title}</strong>
                  <span className="hiw-step-body">{s.body}</span>
                </span>
              </li>
            ))}
          </ol>

          <h3 className="hiw-h">Who can do what</h3>
          <table className="hiw-roles">
            <thead>
              <tr><th>Action</th><th>Creator</th><th>Collaborator</th></tr>
            </thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td><Check on={r.creator} /></td>
                  <td><Check on={r.collaborator} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="hiw-h">Good to know</h3>
          <ul className="hiw-notes">
            <li>You can only build on tools you already have access to. Need one? Request access first, then come back.</li>
            <li>A revealed value is sensitive — don’t paste it into chats, tickets, or screenshots.</li>
            <li>Use the app’s comment thread to ask questions or respond to an admin’s feedback.</li>
          </ul>
        </div>
      </aside>
    </section>
  );
}
